import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, Eye, EyeOff, Mail, Lock } from "lucide-react";
import { api } from "../../lib/api";

const AdminLogin = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  // Restore normal cursor on admin pages
  useEffect(() => {
    document.body.classList.add('admin-cursor');
    return () => document.body.classList.remove('admin-cursor');
  }, []);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const formData = new FormData();
      formData.append("username", email); // OAuth2 still expects 'username' field
      formData.append("password", password);

      const response = await api.post("/token", formData, {
        headers: { "Content-Type": "application/x-www-form-urlencoded" }
      });

      localStorage.setItem("admin_token", response.data.access_token);
      localStorage.setItem("admin_role", response.data.role);
      toast.success("Login successful");
      navigate("/admin/dashboard");
    } catch (error) {
      console.error(error);
      toast.error(error.response?.status === 429
        ? "Too many login attempts. Please wait a few minutes."
        : "Invalid email or password");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-black flex items-center justify-center p-4">
      <div className="bg-neutral-900 border border-white/10 w-full max-w-md p-8 rounded-2xl shadow-2xl">
        <div className="text-center mb-8">
          <h1 className="text-3xl font-display font-bold text-white mb-2">XLUMA ADMIN</h1>
          <p className="text-gray-500 font-mono text-sm uppercase tracking-widest">Authorized Personnel Only</p>
        </div>

        <form onSubmit={handleLogin} className="space-y-6">
          <div className="space-y-2">
            <label className="text-xs font-bold text-accent uppercase tracking-widest flex items-center gap-2">
              <Mail className="w-3 h-3" /> Email
            </label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="admin@xluma.in"
              className="w-full bg-black border border-white/10 p-3 text-white focus:border-accent outline-none font-mono rounded-lg transition-colors"
              required
            />
          </div>

          <div className="space-y-2">
            <label className="text-xs font-bold text-accent uppercase tracking-widest flex items-center gap-2">
              <Lock className="w-3 h-3" /> Password
            </label>
            <div className="relative">
              <input
                type={showPassword ? "text" : "password"}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••••••"
                className="w-full bg-black border border-white/10 p-3 pr-12 text-white focus:border-accent outline-none font-mono rounded-lg transition-colors"
                required
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-accent transition-all duration-300 p-1"
              >
                <div className="relative w-5 h-5">
                  <Eye
                    className={`w-5 h-5 absolute transition-all duration-300 ${showPassword
                      ? 'opacity-0 rotate-90 scale-50'
                      : 'opacity-100 rotate-0 scale-100'
                      }`}
                  />
                  <EyeOff
                    className={`w-5 h-5 absolute transition-all duration-300 ${showPassword
                      ? 'opacity-100 rotate-0 scale-100'
                      : 'opacity-0 -rotate-90 scale-50'
                      }`}
                  />
                </div>
              </button>
            </div>
          </div>

          <button
            disabled={loading}
            className="w-full bg-accent text-black font-bold py-3 uppercase tracking-widest hover:bg-white transition-all duration-300 flex justify-center items-center gap-2 rounded-lg hover:scale-[1.02] disabled:opacity-50 disabled:hover:scale-100"
          >
            {loading && <Loader2 className="animate-spin w-4 h-4" />}
            {loading ? "Authenticating..." : "Secure Login"}
          </button>
        </form>

        <p className="text-gray-600 text-xs text-center mt-6 font-mono">
          Protected by Xluma Security
        </p>
      </div>
    </div>
  );
};

export default AdminLogin;

